import React, { useState, useEffect } from 'react';

/**
 * BreastDiagram - Modern React component to visualize and select breast findings.
 * Replicates the logic from the old web2py app but with SVG interactivity.
 */
const BreastDiagram = ({ value = {}, onChange }) => {
  const [hoveredPos, setHoveredPos] = useState(null);

  // Constants mapping from original web2py logic
  const CX = 229;
  const CY = 118;
  const R = 40; // Clock radius
  const DELTA = 30; // Quadrant offset
  
  const MAMA_OFFSETS = {
    'Derecha': -80,
    'Izquierda': 80
  };

  const getParity = (mama) => (mama === 'Derecha' ? -1 : 1);

  const QUADRANTES = {
    "Superior externo": (p) => [DELTA * p, -DELTA],
    "Superior interno": (p) => [-DELTA * p, -DELTA],
    "Inferior interno": (p) => [-DELTA * p, DELTA],
    "Inferior externo": (p) => [DELTA * p, DELTA],
    "Zona retroareolar": () => [5, 5],
    "Axila": (p) => [3 * DELTA * p, -DELTA]
  };

  const INTERCUADRANTES = {
    "Superior": () => [0, -DELTA],
    "Inferior": () => [0, DELTA],
    "Externo": (p) => [DELTA * p, 0],
    "Interno": (p) => [-DELTA * p, 0]
  };

  // Calculate pixel coordinates for a given finding
  const calculateCoords = (finding) => {
    if (!finding.mama) return null;
    
    let x = CX + MAMA_OFFSETS[finding.mama] || 0;
    let y = CY;
    const p = getParity(finding.mama);

    if (finding.radio) {
      const radioInt = parseInt(finding.radio);
      if (!isNaN(radioInt)) {
        x += R * Math.cos(Math.PI / 2 - radioInt * Math.PI / 6);
        y -= R * Math.sin(Math.PI / 2 - radioInt * Math.PI / 6);
      }
    } else if (finding.cuadrante && QUADRANTES[finding.cuadrante]) {
      const [dx, dy] = QUADRANTES[finding.cuadrante](p);
      x += dx;
      y += dy;
    } else if (finding.linea_intercuadrante && INTERCUADRANTES[finding.linea_intercuadrante]) {
      const [dx, dy] = INTERCUADRANTES[finding.linea_intercuadrante](p);
      x += dx;
      y += dy;
    }

    return { x, y };
  };

  const currentCoords = calculateCoords(value);

  const handleDiagramClick = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Detect which breast was clicked
    const mama = x < CX ? 'Derecha' : 'Izquierda';
    
    // Simple logic: if click is near a radio circle, select radio. 
    // Otherwise, select quadrant based on angle.
    // To make it "Senior", we should ideally have hit-areas.
    // For now, let's just allow clearing or setting the mama.
    onChange({ ...value, mama });
  };

  return (
    <div className="breast-diagram-container" style={{ position: 'relative', width: '458px', height: '236px' }}>
      <img 
        src="/assets/esquema.png" 
        alt="Esquema Mamario" 
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
      
      <svg 
        style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%',
          cursor: 'crosshair'
        }}
        onClick={handleDiagramClick}
      >
        {/* Render current finding marker */}
        {currentCoords && (
          <g>
            <circle 
              cx={currentCoords.x} 
              cy={currentCoords.y} 
              r="6" 
              fill="#ef4444" 
              stroke="#000" 
              strokeWidth="1"
            />
            <circle 
              cx={currentCoords.x} 
              cy={currentCoords.y} 
              r="10" 
              fill="rgba(239, 68, 68, 0.2)"
            />
          </g>
        )}
      </svg>

      <div className="diagram-info" style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
        {value.mama ? (
          <span>
            Marcado: <strong>Mama {value.mama}</strong> 
            {value.radio && ` - Radio ${value.radio}`}
            {value.cuadrante && ` - ${value.cuadrante}`}
          </span>
        ) : (
          <span>Haz clic en una mama para comenzar a marcar.</span>
        )}
      </div>
    </div>
  );
};

export default BreastDiagram;
